// The report view is a STANDARD, engine-agnostic structure — not one component
// per assessment. A rendered report is two independent things:
//
//   1. a COMPOSITION (`ReportConfig`) — an ordered list of section instances an
//      admin authors/reorders/configures per assessment (persisted to
//      `assessment.metadata.reportLayout`; note `metadata.report` is a separate
//      key holding the maturity-stage report prose), and
//   2. a DATA BAG (`ReportModel.data`) — produced by whatever engine ran (the
//      FAIR AI-Era engine, the maturity scorer, or plain answers). Each section
//      instance reads one slice of it via `dataKey`.
//
// Because the two are decoupled, the same section library renders any
// assessment's report, and the AI-Era report is just one seeded `ReportConfig`
// over a `ReportModel` its engine populated. No React here — pure shapes.

import type { DetailLevel } from '../aiEraReport/shared/detail-level';

export type { DetailLevel };

/**
 * Grid placement for a section instance, in `react-grid-layout` units: `x`/`w`
 * are columns (0-based, out of `ReportConfig.gridCols`), `y`/`h` are rows.
 *
 * Optional and additive: a section without a `layout` is placed by
 * `support/reportView/layout.ts` (a full-width stack in array order), so
 * configs authored before the grid builder existed render and export exactly
 * as they did. Reading order — for the screen grid and the PDF alike — is
 * always derived from `(y, x)`, never from pixels, which is what keeps the
 * on-screen report and the PDF in agreement.
 */
export type SectionLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/** One section instance in a report composition. */
export type ReportSectionConfig = {
  /** Stable instance id — keys, reordering, and builder selection. */
  id: string;
  /** Registry key: which section component renders this instance. */
  type: string;
  /** Optional title override (else the section definition's default title). */
  title?: string;
  /** Optional sub-heading hint under the title. */
  hint?: string;
  /** Shallowest detail level at which this instance appears. Defaults to the
   *  section definition's default. */
  minLevel?: DetailLevel;
  /** Which slice of `ReportModel.data` this instance reads. Defaults to the
   *  section definition's `defaultDataKey`. */
  dataKey?: string;
  /** Per-type options (e.g. a recommendations role toggle, a bar limit). */
  options?: Record<string, unknown>;
  /** Grid placement authored in the canvas builder. Absent → auto-placed. */
  layout?: SectionLayout;
};

/** A whole report composition for one assessment. */
export type ReportConfig = {
  /** Show the Executive / Business / Technical detail toggle. Default true. */
  detailLevels?: boolean;
  /** Level to open at. Default 'business'. */
  defaultDetailLevel?: DetailLevel;
  /** Column count the section `layout` coordinates are expressed in. Default 12. */
  gridCols?: number;
  sections: ReportSectionConfig[];
};

/** The engine-produced data a composition renders over. */
export type ReportModel = {
  /** Keyed slices consumed by section instances via `dataKey`. */
  data: Record<string, unknown>;
  /** Free-form context (org name, generatedAt, consolidation mode, …). */
  meta?: Record<string, unknown>;
};

export function isReportConfig(value: unknown): value is ReportConfig {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as ReportConfig).sections)
  );
}
